import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

/** Domain category for a rule row (extend enum on backend when adding types). */
export const ATTENDANCE_RULE_TYPES = ["attendance", "leave", "salary"] as const;
export type AttendanceRuleCategory = (typeof ATTENDANCE_RULE_TYPES)[number];

export const ATTENDANCE_RULE_TYPE_LABELS: Record<AttendanceRuleCategory, string> = {
  attendance: "Attendance",
  leave: "Leave",
  salary: "Salary",
};

export type AttendanceRuleValue = string | number | boolean;

export type AttendanceRuleRecord = {
  id: string;
  title: string;
  description: string;
  code: string;
  value: AttendanceRuleValue;
  type: AttendanceRuleCategory;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type AttendanceRecordRow = {
  id: string;
  employeeId: string;
  employeeName: string | null;
  date: string;
  checkIn: string | null;
  checkOut: string | null;
  lateMinutes: number;
  earlyMinutes: number;
  shortMinutes: number;
  isLate: boolean;
  isEarlyLeave: boolean;
  workedHours: string;
  overtimeMinutes: number;
  offDayOvertimeMinutes: number;
  status: string;
  workShiftId: string | null;
  workShiftName: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AttendanceLogRow = {
  id: string;
  employeeId: string;
  employeeName: string | null;
  punchTime: string;
  type: string;
  source: string | null;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PaginatedMeta = {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
};

type PaginatedResponse<T> = {
  data?: T[];
  meta?: {
    current_page?: number;
    last_page?: number;
    per_page?: number;
    total?: number;
  };
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function num(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function mapRuleValue(raw: unknown): AttendanceRuleValue {
  if (typeof raw === "boolean" || typeof raw === "number") return raw;
  if (typeof raw === "string") return raw;
  return String(raw);
}

function mapRule(raw: Record<string, unknown>): AttendanceRuleRecord {
  const typeRaw = str(raw.type);
  const type = (ATTENDANCE_RULE_TYPES as readonly string[]).includes(typeRaw)
    ? (typeRaw as AttendanceRuleCategory)
    : "attendance";
  return {
    id: str(raw.id),
    title: str(raw.title),
    description: str(raw.description),
    code: str(raw.code),
    value: mapRuleValue(raw.value),
    type,
    isActive: raw.is_active === undefined || raw.is_active === null ? true : Boolean(raw.is_active),
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

function mapRecord(raw: Record<string, unknown>): AttendanceRecordRow {
  return {
    id: str(raw.id),
    employeeId: str(raw.employee_id),
    employeeName: raw.employee_name != null ? str(raw.employee_name) : null,
    date: str(raw.date).slice(0, 10),
    checkIn: raw.check_in != null ? str(raw.check_in) : null,
    checkOut: raw.check_out != null ? str(raw.check_out) : null,
    lateMinutes: num(raw.late_minutes) ?? 0,
    earlyMinutes: num(raw.early_minutes) ?? 0,
    shortMinutes: num(raw.short_minutes) ?? 0,
    isLate: Boolean(raw.is_late),
    isEarlyLeave: Boolean(raw.is_early_leave),
    workedHours: str(raw.worked_hours),
    overtimeMinutes: num(raw.overtime_minutes) ?? 0,
    offDayOvertimeMinutes: num(raw.off_day_overtime_minutes) ?? 0,
    shiftStartOvertimeMinutes: num(raw.shift_start_overtime_minutes) ?? 0,
    shiftEndOvertimeMinutes: num(raw.shift_end_overtime_minutes) ?? 0,
    enableSplitOt: Boolean(raw.enable_split_ot),
    status: str(raw.status),
    workShiftId: raw.work_shift_id != null ? str(raw.work_shift_id) : null,
    workShiftName: raw.work_shift_name != null ? str(raw.work_shift_name) : null,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

function mapLog(raw: Record<string, unknown>): AttendanceLogRow {
  return {
    id: str(raw.id),
    employeeId: str(raw.employee_id),
    employeeName: raw.employee_name != null ? str(raw.employee_name) : null,
    punchTime: str(raw.punch_time),
    type: str(raw.type),
    source: raw.source != null ? str(raw.source) : null,
    comment: raw.comment != null && str(raw.comment) !== "" ? str(raw.comment) : null,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

function mapMeta(meta: PaginatedResponse<unknown>["meta"]): PaginatedMeta {
  return {
    currentPage: meta?.current_page ?? 1,
    lastPage: meta?.last_page ?? 1,
    perPage: meta?.per_page ?? 25,
    total: meta?.total ?? 0,
  };
}

export function attendanceApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export async function fetchAttendanceRulesPage(params?: {
  page?: number;
  perPage?: number;
  type?: AttendanceRuleCategory | "";
}): Promise<{ rows: AttendanceRuleRecord[]; meta: PaginatedMeta }> {
  const response = await api.get<PaginatedResponse<Record<string, unknown>>>("/api/v1/attendance/rules", {
    params: {
      page: params?.page,
      per_page: params?.perPage,
      type: params?.type || undefined,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    rows: list.map((r) => mapRule(r as Record<string, unknown>)),
    meta: mapMeta(response.data?.meta),
  };
}

/** Load every attendance rule (paged) for client-side search/filter like Employees. */
export async function fetchAllAttendanceRules(): Promise<AttendanceRuleRecord[]> {
  const all: AttendanceRuleRecord[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    const { rows, meta } = await fetchAttendanceRulesPage({ page, perPage: 100 });
    all.push(...rows);
    lastPage = Math.max(1, meta.lastPage);
    page += 1;
  } while (page <= lastPage);
  return all;
}

export async function createAttendanceRule(payload: {
  title: string;
  code?: string;
  description?: string;
  type: AttendanceRuleCategory;
  value: AttendanceRuleValue;
  isActive?: boolean;
}): Promise<{ record: AttendanceRuleRecord; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown> }>("/api/v1/attendance/rules", {
    title: payload.title.trim(),
    code: (payload.code ?? "").trim() || undefined,
    description: (payload.description ?? "").trim() || null,
    type: payload.type,
    value: payload.value,
    is_active: payload.isActive !== false,
  });
  const raw = response.data?.data as Record<string, unknown> | undefined;
  return {
    record: mapRule(raw ?? {}),
    message: str(response.data?.message) || "Created.",
  };
}

export async function updateAttendanceRule(
  id: string,
  payload: Partial<{
    title: string;
    code: string;
    description: string;
    type: AttendanceRuleCategory;
    value: AttendanceRuleValue;
    isActive: boolean;
  }>,
): Promise<{ record: AttendanceRuleRecord; message: string }> {
  const body: Record<string, unknown> = {};
  if (payload.title !== undefined) body.title = payload.title.trim();
  if (payload.code !== undefined) body.code = payload.code.trim();
  if (payload.description !== undefined) body.description = payload.description.trim() || null;
  if (payload.type !== undefined) body.type = payload.type;
  if (payload.value !== undefined) body.value = payload.value;
  if (payload.isActive !== undefined) body.is_active = Boolean(payload.isActive);

  const response = await api.patch<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/attendance/rules/${encodeURIComponent(id)}`,
    body,
  );
  const raw = response.data?.data as Record<string, unknown> | undefined;
  return {
    record: mapRule(raw ?? {}),
    message: str(response.data?.message) || "Saved.",
  };
}

export async function deleteAttendanceRule(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message?: string }>(`/api/v1/attendance/rules/${encodeURIComponent(id)}`);
  return { message: str(response.data?.message) || "Deleted." };
}

export type AttendanceProcessStats = {
  recordsUpserted: number;
  daysProcessed: number;
  employeesConsidered: number;
  skippedNoShift: number;
};

export async function processAttendanceRecords(params: {
  dateFrom: string;
  dateTo: string;
  employeeId?: string;
  employeeIds?: string[];
  includeNonWorkingDays?: boolean;
  calendarOnly?: boolean;
}): Promise<{ message: string; stats: AttendanceProcessStats; pendingFinalize: boolean }> {
  const body: Record<string, unknown> = {
    date_from: params.dateFrom,
    date_to: params.dateTo,
  };

  if (params.includeNonWorkingDays) {
    body.include_non_working_days = true;
  }
  if (params.calendarOnly) {
    body.calendar_only = true;
  }
  if (params.employeeIds && params.employeeIds.length > 0) {
    body.employee_ids = params.employeeIds.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0);
  } else if (params.employeeId) {
    body.employee_id = Number(params.employeeId);
  }
  const response = await api.post<{
    message?: string;
    pending_finalize?: boolean;
    data?: {
      records_upserted?: number;
      days_processed?: number;
      employees_considered?: number;
      skipped_no_shift?: number;
    };
  }>("/api/v1/attendance/records/process", body);
  const d = response.data?.data;
  const n = (v: unknown): number => {
    const x = typeof v === "number" ? v : Number(v);
    return Number.isFinite(x) ? x : 0;
  };
  return {
    message: str(response.data?.message) || "Processed.",
    stats: {
      recordsUpserted: n(d?.records_upserted),
      daysProcessed: n(d?.days_processed),
      employeesConsidered: n(d?.employees_considered),
      skippedNoShift: n(d?.skipped_no_shift),
    },
    pendingFinalize: Boolean(response.data?.pending_finalize),
  };
}

export async function finalizeAttendanceRecords(): Promise<{
  message: string;
  recordsFinalized: number;
  pendingFinalize: boolean;
}> {
  const response = await api.post<{
    message?: string;
    pending_finalize?: boolean;
    data?: { records_finalized?: number };
  }>("/api/v1/attendance/records/finalize");
  const n = response.data?.data?.records_finalized;
  return {
    message: str(response.data?.message) || "Finalized.",
    recordsFinalized: typeof n === "number" && Number.isFinite(n) ? n : Number(n) || 0,
    pendingFinalize: Boolean(response.data?.pending_finalize),
  };
}

export async function fetchAttendanceRecords(params: {
  dateFrom?: string;
  dateTo?: string;
  employeeId?: string;
  page?: number;
  perPage?: number;
  finalizedOnly?: boolean;
}): Promise<{ rows: AttendanceRecordRow[]; meta: PaginatedMeta; pendingFinalize: boolean }> {
  const response = await api.get<
    PaginatedResponse<Record<string, unknown>> & { pending_finalize?: boolean }
  >("/api/v1/attendance/records", {
    params: {
      date_from: params.dateFrom,
      date_to: params.dateTo,
      employee_id: params.employeeId,
      page: params.page,
      per_page: params.perPage,
      finalized_only: params.finalizedOnly ? 1 : undefined,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    rows: list.map((r) => mapRecord(r as Record<string, unknown>)),
    meta: mapMeta(response.data?.meta),
    pendingFinalize: Boolean(response.data?.pending_finalize),
  };
}

export type AttendanceMonthGroupRow = {
  id?: string;
  monthKey: string;
  monthLabel: string;
  dateFrom: string;
  dateTo: string;
  status: string;
  processedAt: string | null;
  recordCount: number;
  employeeCount: number;
  totalHours: number;
  presentCount: number;
  absentCount: number;
  leaveCount: number;
  offCount: number;
};

export type AttendanceMonthEmployeeSummaryRow = {
  monthKey: string;
  monthLabel: string;
  employeeId: string;
  employeeName: string;
  workedHours: number;
  overtimeHours: number;
  offDayOvertimeHours?: number;
  offDayOvertimeMinutes?: number;
  shiftStartOvertimeMinutes?: number;
  shiftEndOvertimeMinutes?: number;
  enableSplitOt?: boolean;
  totalHours: number;
  lateCount: number;
  lateMinutes: number;
  presentCount: number;
  halfDayCount: number;
  absentCount: number;
  offCount: number;
  leaveCount: number;
  paidLeaveCount: number;
  unpaidLeaveCount: number;
  offWorkedCount: number;
  holidayCount: number;
  totalDays: number;
  recordCount: number;
};

export async function fetchAttendanceRecordsByMonth(params?: {
  employeeId?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<AttendanceMonthGroupRow[]> {
  const response = await api.get<{ data?: Record<string, unknown>[] }>("/api/v1/attendance/records/by-month", {
    params: {
      employee_id: params?.employeeId,
      date_from: params?.dateFrom,
      date_to: params?.dateTo,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return list.map((raw) => ({
    id: raw.id != null ? str(raw.id) : undefined,
    monthKey: str(raw.month_key),
    monthLabel: str(raw.month_label),
    dateFrom: str(raw.date_from),
    dateTo: str(raw.date_to),
    status: str(raw.status) || "processed",
    processedAt: raw.processed_at != null ? str(raw.processed_at) : null,
    recordCount: num(raw.record_count) ?? 0,
    employeeCount: num(raw.employee_count) ?? 0,
    totalHours: num(raw.total_hours) ?? 0,
    presentCount: num(raw.present_count) ?? 0,
    absentCount: num(raw.absent_count) ?? 0,
    leaveCount: num(raw.leave_count) ?? 0,
    offCount: num(raw.off_count) ?? 0,
  }));
}

export async function fetchAttendanceMonthEmployeeSummaries(params: {
  year: number;
  month: number;
}): Promise<{
  rows: AttendanceMonthEmployeeSummaryRow[];
  meta: { monthKey: string; monthLabel: string; dateFrom: string; dateTo: string };
}> {
  const response = await api.get<{
    data?: Record<string, unknown>[];
    meta?: Record<string, unknown>;
  }>("/api/v1/attendance/records/by-month/employees", {
    params: {
      year: params.year,
      month: params.month,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((raw) => ({
      monthKey: str(raw.month_key),
      monthLabel: str(raw.month_label),
      employeeId: str(raw.employee_id),
      employeeName: str(raw.employee_name),
      workedHours: num(raw.worked_hours) ?? 0,
      overtimeHours: num(raw.overtime_hours) ?? 0,
      offDayOvertimeHours: num(raw.off_day_overtime_hours) ?? 0,
      offDayOvertimeMinutes: num(raw.off_day_overtime_minutes) ?? 0,
      shiftStartOvertimeMinutes: num(raw.shift_start_overtime_minutes) ?? 0,
      shiftEndOvertimeMinutes: num(raw.shift_end_overtime_minutes) ?? 0,
      enableSplitOt: Boolean(raw.enable_split_ot),
      totalHours: num(raw.total_hours) ?? 0,
      lateCount: num(raw.late_count) ?? 0,
      lateMinutes: num(raw.late_minutes) ?? 0,
      presentCount: num(raw.present_count) ?? 0,
      halfDayCount: num(raw.half_day_count) ?? 0,
      absentCount: num(raw.absent_count) ?? 0,
      offCount: num(raw.off_count) ?? 0,
      leaveCount: num(raw.leave_count) ?? 0,
      paidLeaveCount: num(raw.paid_leave_count) ?? 0,
      unpaidLeaveCount: num(raw.unpaid_leave_count) ?? 0,
      offWorkedCount: num(raw.off_worked_count) ?? 0,
      holidayCount: num(raw.holiday_count) ?? 0,
      totalDays: num(raw.total_days) ?? 0,
      recordCount: num(raw.record_count) ?? 0,
    })),
    meta: {
      monthKey: str(meta.month_key),
      monthLabel: str(meta.month_label),
      dateFrom: str(meta.date_from),
      dateTo: str(meta.date_to),
    },
  };
}

export async function deleteAttendanceRecordsByMonth(params: {
  year: number;
  month: number;
}): Promise<{ message: string; recordsDeleted: number }> {
  const response = await api.delete<{
    message?: string;
    data?: { records_deleted?: number };
  }>("/api/v1/attendance/records/by-month", {
    params: {
      year: params.year,
      month: params.month,
    },
  });
  const n = response.data?.data?.records_deleted;
  return {
    message: str(response.data?.message) || "Deleted.",
    recordsDeleted: typeof n === "number" && Number.isFinite(n) ? n : Number(n) || 0,
  };
}

export async function deleteAttendanceLog(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message?: string }>(
    `/api/v1/attendance/logs/${encodeURIComponent(id)}`,
  );
  return { message: str(response.data?.message) || "Deleted." };
}

export async function updateAttendanceLog(payload: {
  id: string;
  punchTimeIso: string;
  type: "in" | "out";
  comment?: string | null;
}): Promise<{ row: AttendanceLogRow; message: string }> {
  const response = await api.put<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/attendance/logs/${encodeURIComponent(payload.id)}`,
    {
      punch_time: payload.punchTimeIso,
      type: payload.type,
      comment: payload.comment ?? null,
    },
  );
  const raw = response.data?.data as Record<string, unknown> | undefined;
  return {
    row: mapLog(raw ?? {}),
    message: str(response.data?.message) || "Punch updated.",
  };
}

export async function createAttendanceLog(payload: {
  employeeId: string;
  type: "in" | "out" | "both";
  timeIn?: string | null;
  timeOut?: string | null;
  source?: string;
}): Promise<{ row: AttendanceLogRow | null; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown> }>("/api/v1/attendance/logs", {
    employee_id: Number(payload.employeeId),
    type: payload.type,
    time_in: payload.timeIn ?? null,
    time_out: payload.timeOut ?? null,
    source: payload.source ?? "manual",
  });
  const raw = response.data?.data as Record<string, unknown> | undefined;
  return {
    row: raw ? mapLog(raw) : null,
    message: str(response.data?.message) || "Punch recorded.",
  };
}

export async function fetchAttendanceLogs(params: {
  dateFrom?: string;
  dateTo?: string;
  employeeId?: string;
  page?: number;
  perPage?: number;
}): Promise<{ rows: AttendanceLogRow[]; meta: PaginatedMeta }> {
  const response = await api.get<PaginatedResponse<Record<string, unknown>>>("/api/v1/attendance/logs", {
    params: {
      date_from: params.dateFrom,
      date_to: params.dateTo,
      employee_id: params.employeeId,
      page: params.page,
      per_page: params.perPage,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    rows: list.map((r) => mapLog(r as Record<string, unknown>)),
    meta: mapMeta(response.data?.meta),
  };
}

export type AttendanceSyncStatus = {
  lastSyncedAt: string | null;
  timezone: string;
};

export type AttendanceSyncSummary = {
  imported: number;
  skipped_unknown_employee: number;
  skipped_duplicate: number;
  skipped_invalid: number;
  devices_synced: number;
  errors: string[];
  last_synced_at: string | null;
  timezone: string;
};

export async function fetchAttendanceSyncStatus(): Promise<AttendanceSyncStatus> {
  const response = await api.get<{
    data?: { last_synced_at?: string | null; timezone?: string };
  }>("/api/v1/attendance/logs/sync-status");

  return {
    lastSyncedAt: response.data?.data?.last_synced_at ?? null,
    timezone: response.data?.data?.timezone ?? "UTC",
  };
}

export async function syncAttendanceFromDevice(): Promise<{
  message: string;
  summary: AttendanceSyncSummary;
}> {
  const response = await api.post<{
    message?: string;
    data?: AttendanceSyncSummary;
  }>("/api/v1/attendance/logs/sync-from-device");

  return {
    message: response.data?.message ?? "Attendance synced from device.",
    summary: response.data?.data ?? {
      imported: 0,
      skipped_unknown_employee: 0,
      skipped_duplicate: 0,
      skipped_invalid: 0,
      devices_synced: 0,
      errors: [],
      last_synced_at: null,
      timezone: "UTC",
    },
  };
}
